import React, { useState, useEffect } from "react";
import {
  Search,
  Upload,
  BarChart3,
  Book,
  HelpCircle,
  Shuffle,
  Music,
  Settings,
} from "lucide-react";

const Header = ({
  activeTab,
  setActiveTab,
  showManual,
  setShowManual,
  darkMode,
  setDarkMode,
  isAuthenticated,
  onLogin,
  onLogout,
  onOpenAISettings,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Mobile layout - slim header with essential controls only (no nav tabs)
  if (isMobile) {
    return (
      <div
        className="bg-forest-900 border-forest-600 border-b sticky top-0 z-50"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        <div className="px-3 py-2">
          <div className="flex items-center justify-between">
            {/* Center: Title */}
            <h1
              className="font-bold gradient-text-gold"
              style={{ fontSize: "1.8rem" }}
            >
              Lyrical-Toolkit
            </h1>

            {/* Right side: Manual + Login/Logout */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowManual(!showManual)}
                className={`p-2 rounded-lg transition-colors ${
                  showManual
                    ? "bg-gold-500 text-forest-950"
                    : "bg-forest-700 text-forest-200 hover:bg-forest-600"
                }`}
                title="Manual"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
              {isAuthenticated ? (
                <button
                  onClick={onLogout}
                  className="px-2 py-1 rounded-lg text-xs font-medium transition-colors bg-ember-500 text-white hover:bg-ember-400"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={onLogin}
                  className="px-2 py-1 rounded-lg text-xs font-medium transition-colors bg-leaf-500 text-forest-100 hover:bg-leaf-400"
                >
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="bg-forest-900 border-forest-600 border-b sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Desktop header */}
        <div style={{ display: "table", width: "100%", marginBottom: "1rem" }}>
          <div
            style={{
              display: "table-cell",
              width: "33.33%",
              verticalAlign: "middle",
            }}
          >
            {/* Spacer - dark mode toggle removed */}
          </div>

          <div
            style={{
              display: "table-cell",
              width: "33.33%",
              verticalAlign: "middle",
              textAlign: "center",
            }}
          >
            <h1 className="text-3xl font-bold gradient-text-gold glow-text-strong">
              Lyrical-Toolkit
            </h1>
          </div>

          <div
            style={{
              display: "table-cell",
              width: "33.33%",
              verticalAlign: "middle",
              textAlign: "right",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem",
              }}
            >
              <button
                onClick={() => setShowManual(!showManual)}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  showManual
                    ? "bg-gold-500 text-forest-950"
                    : "bg-forest-700 text-forest-200 hover:bg-forest-600 hover:text-forest-100"
                }`}
              >
                <HelpCircle className="w-4 h-4 inline mr-2" />
                Manual
              </button>
              {isAuthenticated ? (
                <button
                  onClick={onLogout}
                  className="px-6 py-2 rounded-lg font-medium transition-colors bg-ember-500 text-white hover:bg-ember-400"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={onLogin}
                  className="px-6 py-2 rounded-lg font-medium transition-colors bg-leaf-500 text-forest-100 hover:bg-leaf-400"
                >
                  Login
                </button>
              )}
              {onOpenAISettings && (
                <button
                  onClick={onOpenAISettings}
                  className="p-2 rounded-lg transition-colors bg-forest-700 text-forest-200 hover:bg-forest-600 hover:text-forest-100"
                  title="AI Settings"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop tabs */}
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
            }}
          >
            {[
              "dictionary",
              "synonyms",
              "rhymes",
              "upload",
              "search",
              "analysis",
              "stats",
            ].map((tab) => {
              const icons = {
                search: Search,
                dictionary: Book,
                synonyms: Shuffle,
                rhymes: Music,
                analysis: BarChart3,
                upload: Upload,
                stats: BarChart3,
              };
              const Icon = icons[tab];

              const displayName =
                tab === "upload"
                  ? "Songs"
                  : tab.charAt(0).toUpperCase() + tab.slice(1);
              const isActive = activeTab === tab && !showManual;

              return (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    setShowManual(false);
                  }}
                  className="tab-button"
                  style={{
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                    paddingTop: "0.5rem",
                    paddingBottom: "0.5rem",
                    borderRadius: "0.5rem",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                    border: "1.5px solid #c5a34e",
                    backgroundColor: isActive
                      ? "#152a1c"
                      : "rgba(197, 163, 78, 0.08)",
                    color: isActive ? "#d4b76a" : "#c5a34e",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "120px",
                    backdropFilter: isActive ? "none" : "blur(8px)",
                    WebkitBackdropFilter: isActive ? "none" : "blur(8px)",
                    boxShadow: isActive
                      ? "0 0 12px rgba(197, 163, 78, 0.25), inset 0 0 8px rgba(197, 163, 78, 0.1)"
                      : "none",
                  }}
                >
                  <Icon
                    style={{
                      width: "1rem",
                      height: "1rem",
                      marginRight: "0.5rem",
                    }}
                  />
                  {displayName}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
